import { Suspense } from "react";
import GoIcon from "./icons/GoIcon";
import PythonIcon from "./icons/PythonIcon";
import JavascriptIcon from "./icons/JavascriptIcon";
import ReactIcon from "./icons/ReactIcon";
import CppIcon from "./icons/CppIcon";
import CIcon from "./icons/CIcon";
import ThreejsIcon from "./icons/ThreejsIcon";
import DockerIcon from "./icons/DockerIcon.jsx";
import FlutterIcon from "./icons/FlutterIcon.jsx";
import VideoFallback from "./fallback/VideoFallback";

const projectMap = [
    {
        title: "Karigar Samarthan",
        description: "An NGO-collaborated project developed for low-literacy artisans. Built a Flutter application featuring voice-driven UX for marketplace listings and integrated authenticated REST APIs with WooCommerce backends.",
        video: "/videos/karigar/karigar.mp4", // <-- replace with actual video path
        repoLink: null, // or GitHub link if public
        languages: ["Flutter"],
    },
    {
        title: "PeerDrop",
        description: "A local-first P2P file transfer system featuring real-time LAN discovery via WebSockets, secure one-time download tokens, and direct device streaming. Built using FastAPI, Docker, React, and Nginx with a single-origin architecture for seamless deployment.",
        video: "/videos/peerdrop/peerdrop.mp4", // <-- replace
        repoLink: "https://github.com/0Shree005/PeerDrop.git",
        languages: ["Python", "Docker", "React"],
    },

    {
        title: "DistributedRendering",
        description: "DistributedRendering is a high-performance system built with Go that streamlines complex rendering tasks through a robust client-server architecture. It efficiently manages rendering workload distribution across a locally connected node and employs a custom stack-based rate limiting approach to minimize client-to-server requests.",
        video: "/videos/distributedRendering/DistributedRenderingSceneFull.mp4",
        repoLink: "https://github.com/0Shree005/DistributedRendering",
        languages: ["Go"],
    },
    {
        title: "Galaxy",
        description: "Galaxy is an interactive project built with Three.js that leverages custom shader code to interpolate trigonometric functions and create unique shapes. It showcases dynamic patterns through shader-based animations and offers a live demo for interactive exploration. The project also optimizes shader performance for smooth rendering of evolving shapes.",
        video: "/videos/galaxy/comp-300.mp4",
        repoLink: "https://github.com/0Shree005/Galaxy.git",
        liveDemo: "https://galaxy-0-0.vercel.app/",
        languages: ["ThreeJs"],
    },
    {
        title: "ParticleSystem",
        description: "ParticleSystem is a starter project built with react-three-fiber that demonstrates a basic particle system implementation. It serves as a straightforward introduction to particle system concepts, providing a solid foundation for further experimentation and development.",
        video: "/videos/particleSystem/comp-particleSys.mp4",
        repoLink: "https://github.com/0Shree005/r3fParticleSystem.git",
        liveDemo: "https://r3f-particle-system.vercel.app/",
        languages: ["ThreeJs"],
    },
];

const languageIcons = {
    Go: <GoIcon className="w-6 h-6" />,
    Python: <PythonIcon className="w-6 h-6" />,
    Javascript: <JavascriptIcon className="w-6 h-6" />,
    React: <ReactIcon className="w-6 h-6" />,
    C: <CIcon className="w-6 h-6" />,
    Cpp: <CppIcon className="w-6 h-6" />,
    ThreeJs: <ThreejsIcon className="w-6 h-6" />,
    Docker: <DockerIcon className="w-6 h-6" />,
    Flutter: <FlutterIcon className="w-6 h-6" />,
};

export default function ProjectsContent() {
    return (
        <div className="rounded-sm p-6 backdrop-blur-[5px] sm:backdrop-blur-[5px] shadow-glow pointer-events-auto">
            <h1 className="text-3xl font-bold text-white mb-6">Projects</h1>
            <div className="grid gap-6 pointer-events-auto">
                {projectMap.map((project, index) => (
                    <div
                        key={index}
                        className="p-4 transition-transform hover:scale-105 hover:shadow-glow-lg hover:rounded-sm"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                            {/* Video element */}
                            <Suspense fallback={<VideoFallback />}>
                                <div className="col-span-1">
                                    <div className="bg-slate-700 h-32 w-full rounded-sm flex items-center justify-center shadow-glow">
                                        <video
                                            rel="preload"
                                            preload="metadata"
                                            src={project.video}
                                            className="h-full w-full object-cover rounded-sm"
                                            muted
                                            loop
                                            controls
                                            autoPlay
                                        />
                                    </div>
                                </div>
                            </Suspense>
                            {/* Text content */}
                            <div className="col-span-2">
                                <div className="flex justify-center gap-4">
                                    <h2 className="text-xl font-bold text-sky-300 mb-0.5">
                                        {project.title}
                                    </h2>
                                    {/* Render language icons */}
                                    {project.languages && (
                                        <div className="flex gap-2 mb-2">
                                            {project.languages.map((lang) => (
                                                <span key={lang} className="flex items-center">
                                                    {languageIcons[lang]}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <p className="text-sm text-gray-300 mb-1">
                                    {project.description}
                                </p>
                            {(project.liveDemo || project.repoLink) && (
                                <div className={`grid ${project.liveDemo && project.repoLink ? "grid-cols-2" : "grid-cols-1"} gap-2 sm:max-w-[18vw] items-center mx-auto m-4`}>
                                    {project.liveDemo && (
                                        <a
                                            className="py-2 px-4 shadow-glow rounded-sm active:shadow-glow-lg bg-sky-500 text-white hover:bg-sky-600 transition-colors"
                                            href={project.liveDemo}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Live demo
                                        </a>
                                    )}
                                    {project.repoLink && (
                                        <a
                                            className="py-2 px-4 shadow-glow rounded-sm active:shadow-glow-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors"
                                            href={project.repoLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Repo link
                                        </a>
                                    )}
                                </div>
                            )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
